/**
 * HTTP client — fetch wrapper that targets the future /api/V1/* router
 * (api/index.php -> api/V1/<path>.php), mirroring the ASRPortal pattern.
 *
 *   POST /api/V1/<endpoint>
 *     Content-Type: application/json
 *     X-Requested-With: XMLHttpRequest      (csrf_check.php XHR gate)
 *     X-CS-RFT: <window.__CSRF__>           (session CSRF token)
 *     credentials: 'include'                (PHP session cookie)
 *
 * Responses are the AST envelope `{ err: "1" | "-N", ... }`. On HTTP 401/403
 * or `err: "-401"` we redirect to /login.
 *
 * In Phase 1 the app runs against src/api/mock.ts (VITE_USE_MOCK=1), so these
 * realApi methods aren't exercised yet — but the page/auth layer is written
 * against this exact surface so Phase 2 is a swap, not a rewrite.
 */

import type { BootUser, LoginResult, SaveResult, User } from '@/types/api';

const API = '/api/V1';
const ENDPOINTS = {
  boot:       `${API}/Boot`,
  login:      `${API}/Account/Login`,
  logout:     `${API}/Account/Logout`,
  getUser:    `${API}/Account/GetUser`,
  recover:    `${API}/Account/Recover`,
  resetCheck: `${API}/Account/ResetCheck`,
  reset:      `${API}/Account/Reset`,
};

class HttpError extends Error {
  constructor(public status: number, public body: unknown, message: string) {
    super(message);
  }
}

interface PostJsonOptions {
  /** Don't redirect on auth failure (used by probes). */
  silentOn401?: boolean;
}

function onAuthFailed() {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (path === '/login') return;
  window.location.assign('/login');
}

async function postJson<T = unknown>(
  endpoint: string,
  body?: Record<string, unknown>,
  opts: PostJsonOptions = {},
): Promise<T> {
  const csrf = (typeof window !== 'undefined' && window.__CSRF__) || '';
  const res = await fetch(endpoint, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      'X-CS-RFT': csrf,
      Accept: 'application/json',
    },
    body: body ? JSON.stringify(body) : '',
  });

  if (res.status === 401 || res.status === 403) {
    if (!opts.silentOn401) onAuthFailed();
    throw new HttpError(res.status, null, `HTTP ${res.status}`);
  }

  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (parsed && typeof parsed === 'object' && (parsed as Record<string, unknown>).err === '-401') {
    if (!opts.silentOn401) onAuthFailed();
    throw new HttpError(401, parsed, 'Unauthorized');
  }

  return parsed as T;
}

function bootUserToUser(b: BootUser): User {
  return {
    id: b.id,
    username: b.username,
    name: b.name,
    role: b.role,
    email: b.email ?? undefined,
    orgId: b.orgId ?? undefined,
  };
}

export const realApi = {
  async getMe(): Promise<User> {
    const b = (typeof window !== 'undefined' && window.__USER__) || null;
    if (!b) throw new HttpError(401, null, 'Not authenticated');
    return bootUserToUser(b);
  },

  async login(username: string, password: string): Promise<LoginResult> {
    return postJson<LoginResult>(ENDPOINTS.login, { username, password });
  },

  async logout(): Promise<void> {
    const res = await postJson<{ err?: string | number; csrf?: string }>(
      ENDPOINTS.logout,
      undefined,
      { silentOn401: true },
    );
    // Adopt the fresh CSRF token bound to the new anonymous session so the next
    // login passes its CSRF check without a page refresh.
    if (res && typeof res === 'object' && typeof res.csrf === 'string' && typeof window !== 'undefined') {
      window.__CSRF__ = res.csrf;
    }
  },

  async getUserDetails(userId: number): Promise<User> {
    const res = await postJson<{ err: string; data?: BootUser }>(ENDPOINTS.getUser, { userId });
    if (res.err !== '1' || !res.data) throw new HttpError(400, res, 'GetUser failed');
    return bootUserToUser(res.data);
  },

  /** Request a password-reset email. Always resolves success (no enumeration). */
  async recover(account: string): Promise<SaveResult> {
    return postJson<SaveResult>(ENDPOINTS.recover, { account });
  },

  /** Whether a reset token is still valid (drives the reset page's form vs. expired state). */
  async resetCheck(token: string): Promise<boolean> {
    const res = await postJson<{ err: string; valid?: boolean }>(ENDPOINTS.resetCheck, { token });
    return res.err === '1' && res.valid === true;
  },

  /** Complete a password reset with the emailed token. */
  async resetPassword(token: string, password: string, confirmPassword: string): Promise<SaveResult> {
    return postJson<SaveResult>(ENDPOINTS.reset, { token, password, confirmPassword });
  },
};

export { HttpError, ENDPOINTS, postJson };
